import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Typography, Input, Select } from 'antd';
import { Plus, Check, Trash2 } from 'lucide-react';
import { HtsInputType, HtsYesNoAnswer } from '@evaltick/shared';
import type { HtsQuestionInput } from '@/services/api';
import { Z_INDEX_MODAL } from '@/styles';
import { isNoneOfTheAboveOption } from '@/utils/htsCheckboxOptions';
import styles from './HtsQuestionnairePreviewModal.module.scss';

interface SectionGroup {
  section: string;
  questions: HtsQuestionInput[];
}

const groupBySection = (questions: HtsQuestionInput[]): SectionGroup[] => {
  const order: string[] = [];
  const bySection = new Map<string, HtsQuestionInput[]>();
  questions.forEach((q) => {
    const name = q.section.trim() || 'No section';
    if (!bySection.has(name)) {
      order.push(name);
      bySection.set(name, []);
    }
    bySection.get(name)!.push(q);
  });
  return order.map((section) => ({ section, questions: bySection.get(section) ?? [] }));
};

type PreviewValue = string | string[];
type GroupRow = Record<string, string> & { __rowId: string };

// React key for the always-visible first row before it's been touched. It has
// to survive that row becoming a real, stored row — a key that changes on the
// first keystroke would remount the row and drop the caret out of the input.
const FIRST_ROW_ID = '__preview-first-row__';

const parseGroupRows = (value: PreviewValue | undefined): GroupRow[] => {
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// A row's CHECKBOXES sub-field stores its selected options as a
// JSON-stringified array within that one scalar row value (`GroupRow` is
// string-only, matching how the row array itself is stringified).
const parseCheckboxRowValue = (value: string | undefined): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

interface PreviewControlProps {
  question: HtsQuestionInput;
  value: PreviewValue | undefined;
  onChange: (value: PreviewValue) => void;
}

const PreviewControl: React.FC<PreviewControlProps> = ({ question, value, onChange }) => {
  const stringValue = typeof value === 'string' ? value : undefined;
  const arrayValue = Array.isArray(value) ? value : [];

  switch (question.inputType) {
    case HtsInputType.TEXT:
      return (
        <Input
          value={stringValue ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your answer…"
          className={styles.fieldInput}
        />
      );
    case HtsInputType.TEXTAREA:
      return (
        <Input.TextArea
          value={stringValue ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your answer…"
          className={styles.fieldInput}
          rows={4}
        />
      );
    case HtsInputType.DATE:
      return (
        <input
          type="date"
          value={stringValue ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={styles.dateBox}
        />
      );
    case HtsInputType.SELECT:
      return (
        <Select
          value={stringValue || undefined}
          onChange={(v) => onChange(v)}
          placeholder="Choose one…"
          className={styles.fieldInput}
          options={(question.options ?? []).filter(Boolean).map((option) => ({ value: option, label: option }))}
        />
      );
    case HtsInputType.CHECKBOXES: {
      const options = (question.options ?? []).filter(Boolean);
      const noneOption = options.find(isNoneOfTheAboveOption);
      const noneSelected = !!noneOption && arrayValue.includes(noneOption);

      return (
        <div className={styles.choiceList}>
          {options.map((option) => {
            const selected = arrayValue.includes(option);
            const isNoneOpt = option === noneOption;
            const optionDisabled = noneSelected && !isNoneOpt;
            return (
              <button
                type="button"
                key={option}
                disabled={optionDisabled}
                className={`${styles.choiceOpt} ${selected ? styles.choiceOptSelected : ''}`}
                onClick={() => {
                  if (isNoneOpt) {
                    onChange(selected ? [] : [option]);
                    return;
                  }
                  onChange(selected ? arrayValue.filter((o) => o !== option) : [...arrayValue, option]);
                }}
              >
                <span className={`${styles.choiceBoxSquare} ${selected ? styles.choiceBoxSelected : ''}`}>
                  {selected && <Check style={{ width: '1em', height: '1em' }} />}
                </span>
                {option}
              </button>
            );
          })}
        </div>
      );
    }
    case HtsInputType.YES_NO:
      return (
        <div className={styles.pairRow}>
          <button
            type="button"
            className={`${styles.pairBox} ${value === HtsYesNoAnswer.YES ? styles.pairBoxSelected : ''}`}
            onClick={() => onChange(HtsYesNoAnswer.YES)}
          >
            Yes
          </button>
          <button
            type="button"
            className={`${styles.pairBox} ${value === HtsYesNoAnswer.NO ? styles.pairBoxSelected : ''}`}
            onClick={() => onChange(HtsYesNoAnswer.NO)}
          >
            No
          </button>
        </div>
      );
    case HtsInputType.REPEATABLE_GROUP: {
      const rows = parseGroupRows(value);
      const fields = question.groupFields ?? [];
      const explicitlyNone = value === '[]';
      // Show the first row's fields immediately (mirrors HtsQuestionCard) rather
      // than requiring an "Add row" click. It only becomes a real, stored row
      // once a field in it is touched (see updateRow) — "None" and the row
      // coexist on screen, only "None"'s checked state reflects the answer.
      const firstRow: GroupRow = { __rowId: FIRST_ROW_ID };
      const displayRows = rows.length > 0 ? rows : [firstRow];

      const updateRow = (index: number, key: string, fieldValue: string) => {
        const nextRows = [...displayRows];
        nextRows[index] = { ...nextRows[index], [key]: fieldValue };
        onChange(JSON.stringify(nextRows));
      };
      // Appends to what's on screen, not to `rows` — the untouched first row is
      // unstored, so appending to `rows` would look like the click did nothing.
      const addRow = () => onChange(JSON.stringify([...displayRows, { __rowId: crypto.randomUUID() }]));
      const removeRow = (index: number) => onChange(JSON.stringify(displayRows.filter((_, i) => i !== index)));

      return (
        <div className={styles.groupPreview}>
          {rows.length === 0 && (
            <button
              type="button"
              className={`${styles.choiceOpt} ${explicitlyNone ? styles.choiceOptSelected : ''}`}
              onClick={() => onChange('[]')}
            >
              <span className={`${styles.choiceBoxSquare} ${explicitlyNone ? styles.choiceBoxSelected : ''}`}>
                {explicitlyNone && <Check style={{ width: '1em', height: '1em' }} />}
              </span>
              None
            </button>
          )}
          <div className={styles.groupRowsScroll}>
            {displayRows.map((row, index) => (
              <div key={row.__rowId} className={styles.groupRow}>
                {fields.map((field) => (
                  <div key={field.key} className={styles.groupFieldWrap}>
                    <label className={styles.groupFieldLabel}>{field.label}</label>
                    {field.inputType === HtsInputType.SELECT ? (
                      <Select
                        value={row[field.key] || undefined}
                        onChange={(v) => updateRow(index, field.key, v)}
                        placeholder="Choose one…"
                        className={styles.fieldInput}
                        options={(field.options ?? []).filter(Boolean).map((option) => ({ value: option, label: option }))}
                      />
                    ) : field.inputType === HtsInputType.YES_NO ? (
                      <div className={styles.pairRow}>
                        <button
                          type="button"
                          className={`${styles.pairBox} ${row[field.key] === HtsYesNoAnswer.YES ? styles.pairBoxSelected : ''}`}
                          onClick={() => updateRow(index, field.key, HtsYesNoAnswer.YES)}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className={`${styles.pairBox} ${row[field.key] === HtsYesNoAnswer.NO ? styles.pairBoxSelected : ''}`}
                          onClick={() => updateRow(index, field.key, HtsYesNoAnswer.NO)}
                        >
                          No
                        </button>
                      </div>
                    ) : field.inputType === HtsInputType.CHECKBOXES ? (
                      <div className={styles.choiceList}>
                        {(field.options ?? []).filter(Boolean).map((option) => {
                          const selectedOptions = parseCheckboxRowValue(row[field.key]);
                          const selected = selectedOptions.includes(option);
                          return (
                            <button
                              type="button"
                              key={option}
                              className={`${styles.choiceOpt} ${selected ? styles.choiceOptSelected : ''}`}
                              onClick={() =>
                                updateRow(
                                  index,
                                  field.key,
                                  JSON.stringify(
                                    selected ? selectedOptions.filter((o) => o !== option) : [...selectedOptions, option]
                                  )
                                )
                              }
                            >
                              <span className={`${styles.choiceBoxSquare} ${selected ? styles.choiceBoxSelected : ''}`}>
                                {selected && <Check style={{ width: '1em', height: '1em' }} />}
                              </span>
                              {option}
                            </button>
                          );
                        })}
                      </div>
                    ) : field.inputType === HtsInputType.DATE ? (
                      <input
                        type="date"
                        value={row[field.key] ?? ''}
                        onChange={(e) => updateRow(index, field.key, e.target.value)}
                        className={styles.dateBox}
                      />
                    ) : (
                      <Input
                        value={row[field.key] ?? ''}
                        onChange={(e) => updateRow(index, field.key, e.target.value)}
                        placeholder={field.label}
                        className={styles.fieldInput}
                      />
                    )}
                  </div>
                ))}
                {rows.length > 0 && (
                  <button
                    type="button"
                    className={styles.groupRemoveBtn}
                    onClick={() => removeRow(index)}
                    aria-label="Remove row"
                  >
                    <Trash2 style={{ width: '1em', height: '1em' }} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" className={styles.groupAdd} onClick={addRow}>
            <Plus style={{ width: '1em', height: '1em' }} /> Add row
          </button>
        </div>
      );
    }
    default:
      return null;
  }
};

interface HtsQuestionnairePreviewModalProps {
  open: boolean;
  onClose: () => void;
  name: string;
  questions: HtsQuestionInput[];
}

export const HtsQuestionnairePreviewModal: React.FC<HtsQuestionnairePreviewModalProps> = ({
  open,
  onClose,
  name,
  questions,
}) => {
  const grouped = useMemo(() => groupBySection(questions), [questions]);
  const [values, setValues] = useState<Record<string, PreviewValue>>({});

  useEffect(() => {
    if (open) setValues({});
  }, [open]);

  const setValue = (tempId: string, value: PreviewValue) =>
    setValues((prev) => ({ ...prev, [tempId]: value }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={640}
      title={`Candidate preview — ${name || 'Untitled questionnaire'}`}
      className={styles.modal}
      zIndex={Number(Z_INDEX_MODAL)}
    >
      {questions.length === 0 ? (
        <div className={styles.empty}>No questions yet — add one to see a preview.</div>
      ) : (
        <div className={styles.body}>
          {grouped.map((group) => (
            <div key={group.section} className={styles.section}>
              <Typography.Text className={styles.sectionTitle}>{group.section}</Typography.Text>
              {group.questions.map((q) => (
                <div key={q.tempId} className={styles.question}>
                  <div className={styles.questionText}>{q.text.trim() || <em>Untitled question</em>}</div>
                  <PreviewControl question={q} value={values[q.tempId]} onChange={(v) => setValue(q.tempId, v)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default HtsQuestionnairePreviewModal;
